class ChillCanDeposit {
  constructor() {
    this.depositVariantId = Number(
      window.ChillCanDepositConfig?.depositVariantId || 0
    );

    this.depositProductHandle =
      window.ChillCanDepositConfig?.depositProductHandle ||
      'can-deposit-system';

    this.syncing = false;
    this.syncTimer = null;
    this.cartBadgeObserver = null;
    this.fixingBadge = false;
  }

  init() {
    if (!this.depositVariantId) {
      console.warn('[Chill Deposit] Deposit variant ID is missing.');
      return;
    }

    this.scheduleSync(500);
    this.initCartBadgeGuard();

    document.addEventListener('change', (event) => {
      if (
        event.target.matches(
          '.quantity__input, input[name="updates[]"]'
        )
      ) {
        this.scheduleSync(700);
      }
    });

    document.addEventListener('click', (event) => {
      if (
        event.target.closest(
          '.quantity__button, cart-remove-button, .cart-remove-button'
        )
      ) {
        this.scheduleSync(800);
      }
    });

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'chill-can-deposit') {
          return;
        }

        this.scheduleSync(450);
      });
    }

    document.addEventListener('submit', (event) => {
      const form = event.target;

      if (
        form.closest('product-form') ||
        form.matches('form[action*="/cart/add"]')
      ) {
        this.scheduleSync(1000);
      }
    });

    setTimeout(async () => {
      try {
        const cart = await this.getCart();
        this.updateCartBadge(cart);
      } catch (error) {
        console.error('[Chill Deposit Badge]', error);
      }
    }, 1500);
  }

  scheduleSync(delay = 400) {
    clearTimeout(this.syncTimer);

    this.syncTimer = setTimeout(() => {
      this.sync();
    }, delay);
  }

  async getCart() {
    const cartUrl = window.routes?.cart_url || '/cart';

    const response = await fetch(`${cartUrl}.js`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error('Unable to load cart JSON.');
    }

    return response.json();
  }

  async getCartCanData() {
    const cartUrl = window.routes?.cart_url || '/cart';

    const url =
      `${cartUrl}?section_id=main-cart-items&_=${Date.now()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/html',
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error('Unable to load cart can data.');
    }

    const html = await response.text();

    const documentFragment =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const rows = Array.from(
      documentFragment.querySelectorAll(
        '[data-chill-cart-line]'
      )
    );

    let totalCans = 0;

    rows.forEach((row) => {
      const isDeposit =
        row.dataset.chillDeposit === 'true';

      if (isDeposit) {
        return;
      }

      const cansPerUnit =
        Number(
          row.dataset.cansPerUnit || 0
        );

      const quantity =
        Number(
          row.dataset.quantity || 0
        );

      if (
        cansPerUnit > 0 &&
        quantity > 0
      ) {
        totalCans +=
          cansPerUnit * quantity;
      }
    });

    return totalCans;
  }

  getRealCartItemCount(cart) {
    return cart.items.reduce(
      (total, item) => {
        const isDeposit =
          Number(item.variant_id) ===
            this.depositVariantId ||
          item.properties?._chill_system_fee ===
            'can-deposit';

        if (isDeposit) {
          return total;
        }

        return (
          total +
          Number(item.quantity || 0)
        );
      },
      0
    );
  }

  updateCartBadge(cart) {
    const realItemCount =
      this.getRealCartItemCount(cart);

    const cartIcon =
      document.getElementById(
        'cart-icon-bubble'
      );

    if (!cartIcon) {
      return;
    }

    this.fixingBadge = true;

    try {
      let bubble =
        cartIcon.querySelector(
          '.cart-count-bubble'
        );

      if (realItemCount <= 0) {
        if (bubble) {
          bubble.remove();
        }

        return;
      }

      if (!bubble) {
        bubble =
          document.createElement('div');

        bubble.className =
          'cart-count-bubble';

        bubble.innerHTML = `
          <span aria-hidden="true"></span>
          <span class="visually-hidden"></span>
        `;

        cartIcon.appendChild(bubble);
      }

      const visibleCount =
        bubble.querySelector(
          '[aria-hidden="true"]'
        );

      const hiddenCount =
        bubble.querySelector(
          '.visually-hidden'
        );

      if (visibleCount) {
        visibleCount.textContent =
          realItemCount < 100
            ? String(realItemCount)
            : '';
      }

      if (hiddenCount) {
        hiddenCount.textContent =
          `${realItemCount} ${
            realItemCount === 1
              ? 'item'
              : 'items'
          }`;
      }
    } finally {
      setTimeout(() => {
        this.fixingBadge = false;
      }, 50);
    }
  }

  initCartBadgeGuard() {
    const cartIcon =
      document.getElementById(
        'cart-icon-bubble'
      );

    if (!cartIcon) {
      return;
    }

    if (this.cartBadgeObserver) {
      this.cartBadgeObserver.disconnect();
    }

    const observer =
      new MutationObserver(async () => {
        if (this.fixingBadge) {
          return;
        }

        try {
          const cart =
            await this.getCart();

          this.updateCartBadge(cart);
        } catch (error) {
          console.error(
            '[Chill Deposit Badge]',
            error
          );
        }
      });

    observer.observe(cartIcon, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.cartBadgeObserver =
      observer;
  }

  async updateDeposit(
    cart,
    targetQuantity
  ) {
    const depositItem =
      cart.items.find(
        (item) =>
          Number(item.variant_id) ===
          this.depositVariantId
      );

    const currentQuantity =
      depositItem
        ? Number(depositItem.quantity)
        : 0;

    if (
      currentQuantity ===
      targetQuantity
    ) {
      return false;
    }

    if (depositItem) {
      const response =
        await fetch(
          window.routes
            ?.cart_change_url ||
            '/cart/change.js',
          {
            method: 'POST',
            headers: {
              'Content-Type':
                'application/json',
              Accept:
                'application/json',
            },
            body: JSON.stringify({
              id: depositItem.key,
              quantity:
                targetQuantity,
            }),
          }
        );

      if (!response.ok) {
        throw new Error(
          'Unable to update Can deposit quantity.'
        );
      }

      return true;
    }

    if (targetQuantity > 0) {
      const response =
        await fetch(
          window.routes
            ?.cart_add_url ||
            '/cart/add.js',
          {
            method: 'POST',
            headers: {
              'Content-Type':
                'application/json',
              Accept:
                'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  id:
                    this
                      .depositVariantId,
                  quantity:
                    targetQuantity,
                  properties: {
                    _chill_system_fee:
                      'can-deposit',
                  },
                },
              ],
            }),
          }
        );

      if (!response.ok) {
        const error =
          await response
            .json()
            .catch(() => ({}));

        throw new Error(
          error.description ||
            error.message ||
            'Unable to add Can deposit.'
        );
      }

      return true;
    }

    return false;
  }

  async sync() {
    if (this.syncing) {
      return;
    }

    this.syncing = true;

    try {
      const [cart, totalCans] =
        await Promise.all([
          this.getCart(),
          this.getCartCanData(),
        ]);

      this.updateCartBadge(cart);

      const changed =
        await this.updateDeposit(
          cart,
          totalCans
        );

      document.documentElement
        .dataset.chillTotalCans =
        String(totalCans);

      window.dispatchEvent(
        new CustomEvent(
          'chill:total-cans-updated',
          {
            detail: {
              totalCans,
              deposit:
                Math.round(
                  totalCans * 10
                ) / 100,
            },
          }
        )
      );

      if (!changed) {
        this.updateCartBadge(cart);
        return;
      }

      const updatedCart =
        await this.getCart();

      this.updateCartBadge(
        updatedCart
      );

      if (
        typeof publish ===
          'function' &&
        typeof PUB_SUB_EVENTS !==
          'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        await publish(
          PUB_SUB_EVENTS.cartUpdate,
          {
            source:
              'chill-can-deposit',
            cartData:
              updatedCart,
            variantId:
              this.depositVariantId,
          }
        );
      }

      setTimeout(() => {
        this.updateCartBadge(
          updatedCart
        );
      }, 250);

      setTimeout(() => {
        this.updateCartBadge(
          updatedCart
        );
      }, 800);
    } catch (error) {
      console.error(
        '[Chill Deposit]',
        error
      );
    } finally {
      this.syncing = false;
    }
  }
}

document.addEventListener(
  'DOMContentLoaded',
  () => {
    if (
      window.chillCanDeposit
    ) {
      return;
    }

    window.chillCanDeposit =
      new ChillCanDeposit();

    window.chillCanDeposit.init();
  }
);